import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Users, Loader2, Search, UserCircle, Eye, KeyRound, ShieldOff, ShieldCheck, Store, Truck, Trash2, Building2, MoreVertical } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("active");
  const [blockingId, setBlockingId] = useState(null);
  const [agencyUpdatingId, setAgencyUpdatingId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("gasman_token")}` }
  });

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/app-users`, getAuthHeaders());
      setUsers(response.data);
    } catch (error) {
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleToggleBlock = async (user) => {
    setBlockingId(user.id);
    try {
      const res = await axios.put(`${API}/admin/users/${user.id}/block`, {}, getAuthHeaders());
      setUsers((prev) => prev.map((u) => u.id === user.id ? { ...u, is_blocked: res.data.is_blocked } : u));
      toast.success(res.data.message);
    } catch {
      toast.error("Failed to update user status");
    } finally {
      setBlockingId(null);
    }
  };

  const handleToggleAgency = async (user) => {
    setAgencyUpdatingId(user.id);
    try {
      const response = await axios.put(`${API}/admin/users/${user.id}/agency`, {}, getAuthHeaders());
      setUsers((current) => current.map((item) => (
        item.id === user.id ? { ...item, is_agency: response.data.is_agency } : item
      )));
      toast.success(response.data.message);
    } catch {
      toast.error("Failed to update agency status");
    } finally {
      setAgencyUpdatingId(null);
    }
  };

  const handleDeleteUser = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/users/${deletingUser.id}`, getAuthHeaders());
      setUsers((prev) => prev.filter((u) => u.id !== deletingUser.id));
      toast.success(`${deletingUser.name} has been deleted`);
      setDeleteDialogOpen(false);
      setDeletingUser(null);
    } catch {
      toast.error("Failed to delete user");
    } finally {
      setDeleting(false);
    }
  };

  const viewFilters = {
    active: (user) => !user.is_blocked && user.role !== "delivery_manager" && !user.is_agency,
    agency: (user) => !user.is_blocked && user.is_agency,
    delivery: (user) => user.role === "delivery_manager",
    blocked: (user) => user.is_blocked,
  };
  const filteredUsers = users
    .filter(viewFilters[viewMode])
    .filter((user) => {
      const query = searchTerm.toLowerCase();
      return (user.name || "").toLowerCase().includes(query) ||
        (user.email || "").toLowerCase().includes(query) ||
        (user.phone || "").includes(searchTerm);
    });

  const viewLabels = {
    active: "Active Users",
    agency: "Agency Users",
    delivery: "Delivery Users",
    blocked: "Blocked Users",
  };

  const customerUsers = users.filter((user) => (user.role || "customer") === "customer");
  const userStats = [
    { label: "Total Users", value: customerUsers.length, color: "text-[#0A4CAE]" },
    { label: "Agency Users", value: customerUsers.filter((user) => user.is_agency).length, color: "text-violet-600" },
    { label: "Blocked Users", value: customerUsers.filter((user) => user.is_blocked).length, color: "text-red-600" },
    { label: "Repeat Users", value: customerUsers.filter((user) => (user.total_orders || 0) > 1).length, color: "text-blue-600" },
    { label: "No Order Users", value: customerUsers.filter((user) => (user.total_orders || 0) === 0).length, color: "text-orange-600" },
    { label: "Single Order Users", value: customerUsers.filter((user) => user.total_orders === 1).length, color: "text-emerald-600" },
  ];

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric', month: 'short', day: 'numeric',
    });

  const formatDateTime = (dateString) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const formatPhone = (phone) => {
    if (phone.startsWith("91") && phone.length === 12)
      return `+91 ${phone.slice(2, 7)} ${phone.slice(7)}`;
    return phone;
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(value || 0);

  return (
    <div data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Users className="text-[#0A4CAE]" size={28} />
            Users
          </h1>
          <p className="text-gray-600 mt-1">View and manage registered app users</p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        {userStats.map((stat) => (
          <div key={stat.label} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
            <p className="mt-1 text-sm leading-tight text-gray-500">{stat.label}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {[
          ["active", "Active Users"],
          ["agency", "View Agency Users"],
          ["delivery", "View Delivery Users"],
          ["blocked", "View Blocked Users"],
        ].map(([mode, label]) => (
          <Button
            key={mode}
            variant={viewMode === mode ? "default" : "outline"}
            onClick={() => setViewMode(mode)}
            className={viewMode === mode ? "bg-[#0A4CAE] hover:bg-[#083d8d]" : "bg-white"}
          >
            {label}
          </Button>
        ))}
      </div>

      {/* Search */}
      <div className="mb-6">
        <p className="text-sm font-semibold text-gray-700 mb-2">{viewLabels[viewMode]}</p>
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <Input
            placeholder="Search by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white border-gray-300"
            data-testid="search-users"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-[#0A4CAE]" size={32} />
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-12">
            <Users className="mx-auto text-gray-300 mb-4" size={48} />
            <p className="text-gray-500">
              {searchTerm ? "No users found matching your search" : `No ${viewLabels[viewMode].toLowerCase()} found`}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-bold text-gray-700 w-12">#</TableHead>
                  <TableHead className="font-bold text-gray-700">User</TableHead>
                  <TableHead className="font-bold text-gray-700">Phone</TableHead>
                  <TableHead className="font-bold text-gray-700">Registered On</TableHead>
                  <TableHead className="font-bold text-gray-700">Last Login</TableHead>
                  <TableHead className="font-bold text-gray-700">Active OTP</TableHead>
                  <TableHead className="font-bold text-gray-700">Total Orders</TableHead>
                  <TableHead className="font-bold text-gray-700">Status</TableHead>
                  <TableHead className="font-bold text-gray-700 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user, index) => (
                  <TableRow
                    key={user.id}
                    className={`hover:bg-gray-50 ${user.is_blocked ? "bg-red-50/40" : ""}`}
                    data-testid={`user-row-${user.id}`}
                  >
                    <TableCell className="text-gray-500">{index + 1}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          user.is_blocked ? "bg-red-100"
                          : user.role === "store_admin" ? "bg-orange-50"
                          : user.role === "delivery_manager" ? "bg-blue-50"
                          : "bg-[#0A4CAE]/10"
                        }`}>
                          <UserCircle className={
                            user.is_blocked ? "text-red-400"
                            : user.role === "store_admin" ? "text-[#F7851E]"
                            : user.role === "delivery_manager" ? "text-blue-500"
                            : "text-[#0A4CAE]"
                          } size={24} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-gray-900">{user.name}</p>
                            {user.role === "store_admin" && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-50 text-[#F7851E] border border-orange-200">
                                <Store size={10} /> Store Admin
                              </span>
                            )}
                            {user.role === "delivery_manager" && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-600 border border-blue-200">
                                <Truck size={10} /> Delivery
                              </span>
                            )}
                            {user.role === "customer" && user.is_agency && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-violet-50 text-violet-700 border border-violet-200">
                                <Building2 size={10} /> Agency
                              </span>
                            )}
                          </div>
                          <p className="text-gray-500 text-sm">{user.email}</p>
                          {user.store_name && (
                            <p className="text-xs text-gray-400">{user.store_name}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="text-gray-600 font-medium">{formatPhone(user.phone)}</p>
                      {user.device_type && (
                        <p className="mt-0.5 text-xs text-gray-400">
                          {user.device_type.toLowerCase() === "ios" ? "iOS" : "Android"}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-gray-500 text-sm">{formatDate(user.created_at)}</TableCell>
                    <TableCell className="text-gray-500 text-sm whitespace-nowrap">
                      {formatDateTime(user.last_login_at)}
                    </TableCell>
                    <TableCell>
                      {user.active_otp ? (
                        <span className="inline-flex items-center gap-1.5 font-mono text-sm font-semibold bg-amber-50 text-amber-700 border border-amber-200 rounded-md px-2.5 py-1">
                          <KeyRound size={14} />
                          {user.active_otp}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <p className="font-bold text-gray-900">{user.total_orders || 0}</p>
                      <p className={`mt-0.5 text-xs font-semibold ${
                        (user.total_order_value || 0) > 0 ? "text-green-600" : "text-red-600"
                      }`}>
                        {formatCurrency(user.total_order_value)}
                      </p>
                    </TableCell>
                    <TableCell>
                      {user.is_blocked ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-600">
                          <ShieldOff size={12} /> Blocked
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-600">
                          <ShieldCheck size={12} /> Active
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {user.role !== "delivery_manager" && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-gray-500 hover:text-gray-900"
                                aria-label={`Actions for ${user.name}`}
                                data-testid={`user-actions-${user.id}`}
                              >
                                <MoreVertical size={17} />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-44">
                              <DropdownMenuItem
                                onSelect={() => navigate(`/admin/users/${user.id}`)}
                                data-testid={`view-user-${user.id}`}
                              >
                                <Eye /> View
                              </DropdownMenuItem>
                              {user.role === "customer" && (
                                <DropdownMenuItem
                                  onSelect={() => handleToggleAgency(user)}
                                  disabled={agencyUpdatingId === user.id}
                                >
                                  {agencyUpdatingId === user.id ? <Loader2 className="animate-spin" /> : <Building2 />}
                                  {user.is_agency ? "Remove Agency" : "Mark Agency"}
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                onSelect={() => handleToggleBlock(user)}
                                disabled={blockingId === user.id}
                              >
                                {blockingId === user.id
                                  ? <Loader2 className="animate-spin" />
                                  : user.is_blocked ? <ShieldCheck /> : <ShieldOff />}
                                {user.is_blocked ? "Unblock" : "Block"}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onSelect={() => { setDeletingUser(user); setDeleteDialogOpen(true); }}
                                className="text-red-600 focus:text-red-700 focus:bg-red-50"
                              >
                                <Trash2 /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900">Delete User</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600 mt-2">
            Are you sure you want to permanently delete <span className="font-semibold">{deletingUser?.name}</span>?
            Their account will be removed and they will need to sign up again to use the app.
            Past orders will be retained.
          </p>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="border-gray-300 text-gray-700">
              Cancel
            </Button>
            <Button onClick={handleDeleteUser} disabled={deleting} className="bg-red-500 text-white hover:bg-red-600">
              {deleting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Deleting…</> : "Delete Permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
